<template>
  <div class="flex" style="height: 100%; min-height: 0">
    <aside class="w-1/4 flex-shrink-0 pr-6">
      <nav class="sticky top-4">
        <ul class="space-y-2">
          <li v-for="(item, index) in props.items" :key="index">
            <AwButton
              :class="[
                'w-full text-left px-4 py-2 rounded-md transition-colors',
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                activeIndex === index ? 'bg-gray-200 dark:bg-gray-700 font-medium' : '',
              ]"
              variant="ghost"
              @click="scrollToSection(index)"
            >
              <AwIcon :name="item.icon" :color="activeIndex === index ? 'black' : 'var(--gray-3)'" />
              <div :style="{ color: activeIndex === index ? 'black' : 'var(--gray-3)' }">
                {{ item.label }}
              </div>
            </AwButton>
          </li>
        </ul>
      </nav>
    </aside>

    <div class="w-px bg-gray-300 dark:bg-gray-700 flex-shrink-0" :style="{ height: separatorHeight + 'px' }"></div>

    <div
      ref="contentContainer"
      :class="['w-3/4 flex-1 pl-6 overflow-y-auto space-y-8', props.sectionClass || 'pb-32']"
      style="height: 100%; scrollbar-gutter: stable"
    >
      <section
        v-for="(item, index) in props.items"
        :id="`section-${index}`"
        :key="index"
        :ref="(el) => setSectionRef(el, index)"
        class="scroll-mt-4"
      >
        <slot :name="item.slot"></slot>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useResizeObserver, useScroll, watchThrottled } from "@vueuse/core";

interface DetailItem {
  label: string;
  icon: string;
  slot: string;
}

const props = defineProps<{
  sectionClass?: string;
  items: DetailItem[];
}>();

const activeIndex = ref<number>(0);
const sectionRefs = ref<(HTMLElement | null)[]>([]);
const contentContainer = ref<HTMLElement | null>(null);
const separatorHeight = ref<number>(0);

const setSectionRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (el && el instanceof HTMLElement) {
    sectionRefs.value[index] = el;
  } else if (el && "$el" in el && el.$el instanceof HTMLElement) {
    sectionRefs.value[index] = el.$el;
  }
};

useResizeObserver(contentContainer, () => {
  if (contentContainer.value) {
    separatorHeight.value = contentContainer.value.scrollHeight;
  }
});

const scrollToSection = (index: number) => {
  const section = sectionRefs.value[index];
  if (section && contentContainer.value) {
    activeIndex.value = index;
    const containerTop = contentContainer.value.getBoundingClientRect().top;
    const sectionTop = section.getBoundingClientRect().top;
    const scrollPosition = contentContainer.value.scrollTop + sectionTop - containerTop - 16;

    contentContainer.value.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  }
};

const { y: scrollY } = useScroll(contentContainer);

watchThrottled(
  scrollY,
  () => {
    if (!contentContainer.value || sectionRefs.value.length === 0) return;

    const containerTop = contentContainer.value.getBoundingClientRect().top;
    const offset = 16;

    let closestIndex = 0;
    let minDistance = Infinity;

    sectionRefs.value.forEach((section, index) => {
      if (!section) return;

      const sectionTop = section.getBoundingClientRect().top;
      const distance = Math.abs(sectionTop - containerTop - offset);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    activeIndex.value = closestIndex;
  },
  { immediate: true, throttle: 100 }
);

onMounted(() => {
  if (contentContainer.value) {
    separatorHeight.value = contentContainer.value.scrollHeight;
  }
});
</script>
